/**
 * Admin endpoint client.
 *
 * Thin wrapper around `services/api.ts` so each tab/action can call a typed
 * function instead of hard-coded paths. Mirrors handlers in
 * `~/Projects/stagery-api/api/admin/*.js`.
 */

import { api } from '../../services/api';
import type {
  AdminStats,
  AdminUser,
  RevenueData,
  KantoorRow,
  UsageRow,
  PropertyRow,
  RenderRow,
  InviteRow,
  FeedbackRow,
  CorrectionFeedbackRow,
  EmailLogRow,
  CodeRow,
  ReferralRow,
  ActivityResponse,
  GlobalPreferencesResponse,
  PreferencesHealthResponse,
  PreferencesUsersResponse,
  Role,
} from './types';

// ─── Reads ─────────────────────────────────────────────────────────────

export const adminApi = {
  stats: () => api.get<AdminStats>('/api/admin/stats'),
  revenue: () => api.get<RevenueData>('/api/admin/revenue'),
  users: () => api.get<AdminUser[]>('/api/admin/users'),
  user: (id: number) => api.get<AdminUser>(`/api/admin/users/${id}`),
  properties: () => api.get<PropertyRow[]>('/api/admin/properties'),
  renders: () => api.get<RenderRow[]>('/api/admin/renders'),
  codes: () => api.get<CodeRow[]>('/api/admin/codes'),
  feedback: () => api.get<FeedbackRow[]>('/api/admin/feedback'),
  correctionFeedback: () => api.get<CorrectionFeedbackRow[]>('/api/admin/correction-feedback'),
  emailLogs: (limit = 200) => api.get<EmailLogRow[]>(`/api/admin/email-logs?limit=${limit}`),
  sentInvites: () => api.get<InviteRow[]>('/api/admin/sent-invites'),
  usage: () => api.get<UsageRow[]>('/api/admin/usage'),
  referrals: () => api.get<ReferralRow[]>('/api/admin/referrals'),
  activity: () => api.get<ActivityResponse>('/api/admin/activity'),
  kantoren: () => api.get<KantoorRow[]>('/api/admin/kantoren'),
  globalPreferences: () => api.get<GlobalPreferencesResponse>('/api/admin/global-preferences'),
  preferencesHealth: () => api.get<PreferencesHealthResponse>('/api/admin/preferences/health'),
  preferencesUsers: () => api.get<PreferencesUsersResponse>('/api/admin/preferences/users'),

  // ─── Writes ────────────────────────────────────────────────────────
  createCode: (body: { note?: string; address?: string; expiresAt?: string; invitedName?: string; invitedCompany?: string }) =>
    api.post<CodeRow>('/api/admin/codes', body),
  revokeCode: (id: number) => api.post<{ ok: true }>(`/api/admin/codes/${id}/revoke`, {}),

  setRole: (userId: number, role: Role) =>
    api.post<{ id: number; email: string; role: Role }>('/api/admin/set-role', { userId, role }),

  bulkCredits: (userIds: number[], credits: number) =>
    api.post<{ updated: number; users: { id: number; email: string; credits: number }[] }>('/api/admin/bulk-credits', { userIds, credits }),

  bulkDelete: (userIds: number[]) =>
    api.post<{ deleted: number; users: { id: number; email: string }[] }>('/api/admin/bulk-delete-users', { userIds }),

  impersonate: (userId: number) =>
    api.post<{ token: string; expires_at: string; target: { id: number; email: string } }>(`/api/admin/impersonate/${userId}`, {}),

  invite: (body: { email: string; role?: Role; company?: string }) =>
    api.post<{ ok: true; invite: InviteRow }>('/api/admin/invite', body),

  mergeDuplicate: (email: string, dryRun = true) =>
    api.post<{ message?: string; user?: unknown; orphans?: unknown; migrated?: unknown }>('/api/admin/merge-duplicate-user', { email, dryRun }),

  resetPreferences: (userId: number, reason: string) =>
    api.post<{ ok: true }>('/api/admin/preferences/reset', { userId, reason }),

  patchUser: (id: number, body: { credits?: number; role?: Role; isActive?: boolean }) =>
    api.patch<AdminUser>(`/api/admin/users/${id}`, body),

  deleteUser: (id: number) => api.del<{ ok: true }>(`/api/admin/users/${id}`),

  deleteProperty: (id: number) => api.del<{ ok: true; deleted: { id: number; address: string } }>(`/api/admin/properties/${id}`),
};
